<template>
  <v-app-bar app elevate-on-scroll clipped-left color="header">
    <v-app-bar-nav-icon @click="$emit('nav-icon-click')" class="ml-n6 mr-3"></v-app-bar-nav-icon>
    <a href="/">
      <img class="app-logo" alt="veroFresh" src="/img/veroFresh.png">
    </a>
    <v-menu
      v-if="hasCustomerContext"
      v-model="showCustomerContextMenu"
      offset-x :close-on-content-click="false"
      @input="openCloseCustomerContextMenu($event)">
      <template v-slot:activator="{ on, attrs }">
        <span class="ml-10 d-inline-flex align-center">
          <span v-if="$vuetify.breakpoint.mdAndUp" class="mr-1 text-subtitle-1">{{ $t('account') }}: </span>
          <v-btn v-bind="attrs" v-on="on" text color="primary-900">
            <span class="text-subtitle-1">{{ customerContext.name }}</span>
            <v-icon v-if="size(user.customers) > 1">mdi-chevron-right</v-icon>
          </v-btn>
        </span>
      </template>
      <customer-context-selector ref="customerContextSelector" @change-context="showCustomerContextMenu = false"/>
    </v-menu>
    <v-spacer></v-spacer>
    <v-switch
      v-if="showNewUISwitch"
      v-model="newUIEnabled"
      @change="onToggleNewUI"
      color="primary"
      hide-details
      dense
      class="new-ui-switch mt-0 mr-4"
      label="New UI"
    ></v-switch>
    <v-menu offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" icon color="primary-900">
          <v-icon>mdi-account-circle-outline</v-icon>
        </v-btn>
      </template>
      <v-card>
        <v-list>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ user.full_name }}</v-list-item-title>
              <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-btn @click="handleLogout" elevation="1">
              <v-icon class="mr-1">mdi-logout</v-icon>
              {{ $t('log_out') }}
            </v-btn>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
    <v-menu offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" icon color="primary-900">
          <v-icon>mdi-cog</v-icon>
        </v-btn>
      </template>
      <v-card>
        <v-list>
          <v-subheader>{{ $t('settings') }}</v-subheader>
          <v-list-item-group>
            <v-list-item to="/user/settings">
              <v-list-item-icon>
                <v-icon>mdi-account-cog</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('user_settings') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
          <v-subheader v-if="user.can('view_rootentity')">{{ $t('internal_only') }}</v-subheader>
          <v-list-item-group v-if="user.can('view_rootentity') && showDevMenu">
            <v-list-item @click="resetTranslations()">
              <v-list-item-icon>
                <v-icon>mdi-translate</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Reset Translation Data</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
          <v-subheader>{{ appVersion }}</v-subheader>
        </v-list>
      </v-card>
    </v-menu>
  </v-app-bar>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import CustomerContextSelector from '@/components/navigation/CustomerContextSelector'
import AuthUtil from '@/util/AuthUtil'
import UrlHelper from '@/util/Url'
import Cookie from 'js-cookie'
import { size } from 'lodash'

// Long-lived, cross-subdomain preference cookie read by both the Vue 2 and the
// new Vue 3 app (and any future server-side routing) to decide which UI to show.
const UI_PREF_COOKIE = 'vf_ui_pref'

// Customer setting that opts a customer in to the Vue 3 UI, so the rollout can
// be widened per customer from the DB (customer_setting row) without a release.
// Internal (root) users always see the toggle regardless of this setting.
const NEW_UI_SETTING = 'enableNewUI'

/**
 * Loose "on" check tolerant of the 1 / '1' / true shapes customer settings take
 * (mirrors flagOn() in vue3/src/components/navigation/navItems.js).
 *
 * @param {*} value
 * @return {Boolean}
 */
function flagOn (value) {
  return value === 1 || value === '1' || value === true
}

export default {
  name: 'TopBar',
  components: {
    CustomerContextSelector
  },
  mixins: [
    CurrentUserMixin
  ],
  data () {
    const uiPref = Cookie.get(UI_PREF_COOKIE)
    return {
      showCustomerContextMenu: false,
      // Reflect the persisted preference on landing. We intentionally do NOT
      // auto-redirect a Vue 2 visitor whose cookie says 'v3' (route parity with
      // the new app doesn't exist yet); the toggle just mirrors the saved state.
      //
      // The cookie is the ONLY cross-app source of truth: it is the single
      // thing both SPAs read and write. `userModule.useNewUI` is persisted to
      // this app's own localStorage, which the Vue 3 app cannot clear, so once
      // it had been flipped on it kept the toggle stuck ON even after the user
      // switched back to the classic UI from v3 (which writes
      // vf_ui_pref=legacy). Consult the store only when there is no cookie.
      newUIEnabled: uiPref ? uiPref === 'v3' : this.$store.getters['userModule/useNewUI']
    }
  },
  computed: {
    /**
     * Return a string representation of the application version (e.g. v1.2.3)
     * @return string
     */
    appVersion () {
      let appVersion = this.$store.getters['appMetadataModule/version']
      // prepend a "v" when the version is a semantic version (it looks funky if we're showing the stage)
      if (appVersion !== 'pre-build') {
        appVersion = 'v' + appVersion
      }
      return appVersion
    },
    showDevMenu () {
      return this.appVersion === 'pre-build' && this.user.can('view_rootentity')
    },
    /**
     * Whether to offer the "New UI" toggle at all.
     *
     * The toggle sends the user to a separate SPA under /v3/ which is only
     * partially migrated (and which 404s entirely in any environment where the
     * vue3 build has not been deployed), so it is not something to put in front
     * of every customer user. Two existing gating mechanisms are combined:
     *
     *  - internal users: the `view_rootentity` permission, the same permission
     *    that guards the internal-only section of the settings menu above; and
     *  - customers: the `enableNewUI` customer setting, so the rollout can be
     *    widened one customer at a time from the DB (same pattern as
     *    enableViolationRootCauses / routePlanFeedEnabled in SideNav).
     *
     * Hidden for every user while feature.forceNewUI.enabled is on, and for a
     * root user while feature.forceNewUIForRoot.enabled is on: AppLayout sends
     * them to /v3, so there is nothing to switch.
     *
     * @return Boolean
     */
    showNewUISwitch () {
      if ((this.isRootUser && this.$store.getters['featureModule/forceNewUIForRoot']) ||
        this.$store.getters['featureModule/forceNewUI']) {
        return false
      }
      return this.user.can('view_rootentity') ||
        flagOn(this.customerContext.getSetting(NEW_UI_SETTING))
    }
  },
  methods: {
    size: size,
    /**
     * Reset the search value in the customer context selector when the menu is opened.
     * Since v-menu doesn't give us an event when the menu is actually fully opened, we have to make do with checking
     * to see if the value was changed to true and if the component ref exists yet. On the first open, it will probably
     * not exist, but the value won't need to be cleared. If re-opened again, the component will already exist and we
     * can then reset it as desired.
     * @param event
     */
    openCloseCustomerContextMenu (event) {
      if (event && this.$refs.customerContextSelector) {
        this.$refs.customerContextSelector.resetSearch()
      }
    },
    /**
     * Log the user out.
     */
    handleLogout () {
      AuthUtil.logout()
      this.$router.push('/login')
    },
    resetTranslations () {
      const parsed = JSON.parse(localStorage.getItem('veroFreshApp'))
      delete parsed.localeModule
      localStorage.setItem('veroFreshApp', JSON.stringify(parsed))
      window.location.reload()
    },
    /**
     * Handle the "New UI" toggle. Persists the choice to the store and to a
     * long-lived cross-subdomain cookie so both apps can read it, then (when
     * enabling) hands off to the Vue 3 app under /v3/, preserving the current
     * path + query so it can attempt the equivalent page. Its catch-all route
     * handles anything not yet migrated.
     *
     * @param {Boolean} value the new toggle state
     */
    onToggleNewUI (value) {
      this.$store.dispatch('userModule/setUseNewUI', value)
      Cookie.set(UI_PREF_COOKIE, value ? 'v3' : 'legacy', {
        expires: 365,
        domain: UrlHelper.getCookieDomain()
      })
      if (value) {
        // Full page load: /v3/ is a separate SPA, not a vue-router route.
        // this.$route.fullPath starts with '/', so this yields /v3/<path>?<query>.
        window.location.assign('/v3' + this.$route.fullPath)
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .app-logo {
    // 1.79 rem at 14px (per style guide)
    height: 25px;
  }
</style>

<style lang="scss">
  // Hack the style of the toolbar to match the style guide
  .verofresh .v-toolbar__content {
    // 2.14 rem at 14px
    padding: 4px 30px;
  }
  // Bit of a hack to get the app-bar extension area to look the way we want it to.
  .v-toolbar__extension {
    padding: 0 !important;
  }
</style>
